import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { Box, Paper, Typography } from '@mui/material';
import { expandRecurringMeetings, taskToMeetingEvent, buildWeekDays } from './calendarUtils';

/**
 * Flattens the time_logs panel payload into individual logs.
 * @param {Array<object>} groups
 */
const flattenLogs = (groups = []) =>
  groups.flatMap((g) => g.action_items.flatMap((ai) => ai.logs || []));

/**
 * 7-column week overview. Each day shows its meetings, task count and hours
 * logged. Clicking a day drills into Day mode for that date.
 *
 * @param {object} props
 * @param {dayjs.Dayjs} props.currentDate
 * @param {object} props.data - The /calendar payload { meetings, do_today, time_logs }.
 * @param {object} props.toggles - { meetings, do_today, time_logs } booleans.
 * @param {function} props.onSelectDay - (dayjs) => void
 */
const WeekGrid = ({ currentDate, data, toggles, onSelectDay }) => {
  const days = useMemo(() => buildWeekDays(currentDate), [currentDate]);

  const meetingsByDay = useMemo(() => {
    const expanded = expandRecurringMeetings(data?.meetings?.events || []);
    const taskEvents = (data?.meetings?.tasks || []).map(taskToMeetingEvent);
    return [...expanded, ...taskEvents];
  }, [data]);

  const logs = useMemo(() => flattenLogs(data?.time_logs), [data]);

  const cellFor = (day) => {
    const dayMeetings = meetingsByDay.filter((m) => dayjs(m.start_date).isSame(day, 'day'));
    const taskCount = (data?.do_today || []).filter((t) =>
      dayjs(t.do_date).isSame(day, 'day'),
    ).length;
    const hours = logs
      .filter((l) => dayjs(l.start_time).isSame(day, 'day'))
      .reduce((sum, l) => sum + parseFloat(l.duration || 0), 0);
    return { dayMeetings, taskCount, hours };
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
      {days.map((day) => {
        const { dayMeetings, taskCount, hours } = cellFor(day);
        return (
          <Paper
            key={day.format('YYYY-MM-DD')}
            variant="outlined"
            onClick={() => onSelectDay(day)}
            sx={{
              p: 1,
              minHeight: 160,
              cursor: 'pointer',
              bgcolor: day.isSame(dayjs(), 'day') ? 'action.hover' : 'background.paper',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 'bold' }} display="block">
              {day.format('ddd D')}
            </Typography>

            {toggles.meetings &&
              dayMeetings.map((m) => (
                <Box
                  key={m.id}
                  sx={{
                    bgcolor: m.isTask ? 'secondary.main' : 'primary.main',
                    color: 'white',
                    px: 0.5,
                    borderRadius: 0.5,
                    mt: 0.5,
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {m.start_time ? `${dayjs(`${m.start_date}T${m.start_time}`).format('HH:mm')} ` : ''}
                  {m.meeting_name}
                </Box>
              ))}

            <Box sx={{ mt: 1 }}>
              {toggles.do_today && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {taskCount} task{taskCount === 1 ? '' : 's'} due
                </Typography>
              )}
              {toggles.time_logs && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {hours.toFixed(2)}h logged
                </Typography>
              )}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default WeekGrid;
